/** A signer's place in the signing order and their own deadline, if they have one. */
export interface SignerDeadline {
  order: number
  expiresAt?: string | null | undefined
}

export interface SignerDeadlineIssue {
  /** The signer's position in the list checked. */
  index: number
  code: 'signer.expires_after_envelope' | 'signer.expires_before_earlier_signer'
  message: string
}

/**
 * The rules a signer's own deadline keeps. It can't be after the envelope's,
 * which ends everything. It can't be before the deadline of a signer earlier in
 * the order: that signer finishing late, but in time, would leave this one
 * expired before their turn. A signer without their own has the envelope's.
 */
export function signerDeadlineIssues(
  envelopeExpiresAt: string,
  signers: readonly SignerDeadline[],
): SignerDeadlineIssue[] {
  const envelopeAt = Date.parse(envelopeExpiresAt)
  const deadlines = signers.map((signer, index) => ({
    index,
    order: signer.order,
    own: Boolean(signer.expiresAt),
    at: signer.expiresAt ? Date.parse(signer.expiresAt) : envelopeAt,
  }))
  const issues: SignerDeadlineIssue[] = []
  for (const deadline of deadlines) {
    if (deadline.own && deadline.at > envelopeAt) {
      issues.push({
        index: deadline.index,
        code: 'signer.expires_after_envelope',
        message: 'A signer’s expiresAt can’t be after the envelope’s',
      })
      continue
    }
    if (deadlines.some((other) => other.order < deadline.order && other.at > deadline.at)) {
      issues.push({
        index: deadline.index,
        code: 'signer.expires_before_earlier_signer',
        message: 'A signer’s expiresAt can’t be before that of a signer earlier in the order',
      })
    }
  }
  return issues
}
